import { loggerUtils } from '../Logging';

import type { IBrowserStoragePayloadWrapper, IBrowserStoragePersistenceOptions } from './types';

// Note that some browser extensions disable localStorage, sessionStorage and/or cookies.
// See: https://getcolor.atlassian.net/browse/UITASK-131
// On some browser, referencing browser storage throws SecurityError: The operation is insecure.
let nullableLocalStorage: Storage | null = null;
let nullableSessionStorage: Storage | null = null;
try {
  nullableLocalStorage = window.localStorage;
} catch {
  // Intentionally ignore this error, since some users disable localStorage using browser extensions or configs.
}
try {
  nullableSessionStorage = window.sessionStorage;
} catch {
  // Intentionally ignore this error, since some users disable sessionStorage using browser extensions or configs.
}

export enum BrowserStorageType {
  LOCAL_STORAGE,
  SESSION_STORAGE,
}

function getBrowserStorage(browserStorageType: BrowserStorageType): Storage | null {
  switch (browserStorageType) {
    case BrowserStorageType.LOCAL_STORAGE:
      return nullableLocalStorage;
    case BrowserStorageType.SESSION_STORAGE:
      return nullableSessionStorage;
    default:
      throw new Error(`Unknown Browser storage type: ${browserStorageType}`);
  }
}

export function isPayloadValid<T>(
  payloadWrapper: IBrowserStoragePayloadWrapper<T>,
  options: IBrowserStoragePersistenceOptions
): boolean {
  const { validTimeInMilliseconds = Number.MAX_VALUE } = options;
  const { updatedAt } = payloadWrapper;
  return !!(updatedAt && Date.now() - updatedAt <= validTimeInMilliseconds);
}

export function getValidBrowserStoragePayload<T>(
  browserStorageType: BrowserStorageType,
  key: string,
  initialValue: T,
  options: IBrowserStoragePersistenceOptions
): T {
  try {
    const item = getBrowserStorage(browserStorageType)?.getItem(key);
    const payloadWrapper = item && (JSON.parse(item) as IBrowserStoragePayloadWrapper<T>);
    if (payloadWrapper && isPayloadValid<T>(payloadWrapper, options)) {
      return payloadWrapper.payload;
    }
    return initialValue;
  } catch {
    return initialValue;
  }
}

export function setBrowserStoragePayload<T>(
  browserStorageType: BrowserStorageType,
  key: string,
  payload: T
): void {
  try {
    const payloadWrapper: IBrowserStoragePayloadWrapper<T> = {
      updatedAt: Date.now(),
      payload,
    };
    getBrowserStorage(browserStorageType)?.setItem(key, JSON.stringify(payloadWrapper));
  } catch (error) {
    loggerUtils.logException(error as Error);
  }
}

export function clearBrowserStoragePayload(
  browserStorageType: BrowserStorageType,
  key: string
): void {
  try {
    getBrowserStorage(browserStorageType)?.removeItem(key);
  } catch (error) {
    loggerUtils.logException(error as Error);
  }
}

export function clearBrowserStorage(browserStorageType: BrowserStorageType): void {
  try {
    getBrowserStorage(browserStorageType)?.clear();
  } catch (error) {
    loggerUtils.logException(error as Error);
  }
}
